import type {
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects,
  TouchSensor,
} from '@dnd-kit/core'
import moment from 'moment'
import { rem } from 'polished'
import type { CSSProperties } from 'react'
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { set as setLocalStorage, get as getLocalStorage } from 'lib/storage/isomorphicLocalStorage'

import { LARGE_DESKTOP_WHITESPACE_OFFSET } from './common'

import LoadingBox from 'components/Common/Loading/LoadingBox'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import useSyncedState from 'hooks/useSyncedState'
import { arrayToObject } from 'lib/array/arrayUtils'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import zIndex from 'styles/tools/z-index'
import DraggedItemHeightContext from 'tripPlanner/components/DragAndDrop/DraggedItemHeightContext'
import { itineraryCollisionDetection } from 'tripPlanner/components/DragAndDrop/itineraryCollisionDetection'
import { restrictToItineraryDate } from 'tripPlanner/components/DragAndDrop/restrictToItineraryDate'
import WhileDraggingStyle from 'tripPlanner/components/DragAndDrop/WhileDraggingStyle'
import ItineraryBlockTrigger from 'tripPlanner/components/Itinerary/ItineraryBlockTrigger'
import SectionHeading from 'tripPlanner/components/Timeline/SectionHeading'
import RegenerateButton from 'tripPlanner/components/Buttons/RegenerateButton'
import { useRankTripItem } from 'tripPlanner/hooks/api/tripItem'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import SortableItineraryItems from 'tripPlanner/pages/trip/Itinerary/SortableItineraryItems'
import type { TripItem } from 'tripPlanner/types/tripItem'
import type { ItinerarySection } from 'tripPlanner/utils/itinerary'
import {
  addIntermediateAndEndItems,
  NO_DATES,
  updateItemStartDate,
  itinerarySectionToString,
  SUGGESTED,
  itinerarySectionElementId,
} from 'tripPlanner/utils/itinerary'
import type { FullTrip } from 'tripPlanner/types/common'
import SortableNonDateItems from './SortableNonDateItems'
import SuggestedSection from './Suggestions/SuggestedSection'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import InlineItemCreateProvider from 'tripPlanner/contexts/InlineItemCreate/InlineItemCreateProvider'
import ItineraryInfoCard from 'tripPlanner/components/CleanCard/ItineraryInfoCard'
import ReplanFeedback from 'tripPlanner/components/Timeline/ReplanFeedback'
import SuggestReplanDayBanner from 'tripPlanner/components/Timeline/SuggestReplanDayBanner'

const DROP_ANIMATION_DURATION = 150
const SUGGEST_REPLAN_BANNER_DISMISSED_KEY = 'suggestReplanBanner.dismissed'

const dropAnimationSideEffects = defaultDropAnimationSideEffects({
  className: {
    dragOverlay: 'dropped',
  },
  styles: {
    active: {
      visibility: 'hidden',
    },
  },
})

const DayContainer = styled.div`
  @media screen {
    min-height: var(--screenOnlyMinHeight);
  }
`

const LoadingDay = styled(LoadingBox)`
  height: ${rem(68)};

  &.colour-neutral {
    --loading-box-bg-color: white;
  }
`

interface Props {
  trip: FullTrip
  items: Array<TripItem>
  startItemsBySection: Array<{
    date: ItinerarySection
    startItems: Array<TripItem>
  }>
  onSectionVisibilityChange: (section: string, isVisible: boolean) => void
  headerOffset: number
  tripLoading: boolean
}

function SortableItineraryDays({
  trip,
  items,
  startItemsBySection,
  onSectionVisibilityChange,
  headerOffset,
  tripLoading,
}: Props) {
  const intl = useIntl()
  const tripId = trip.id
  const [regeneratingDays, setRegeneratingDays] = useState<Set<string>>(new Set())
  const [replannedDays, setReplannedDays] = useState<Set<string>>(new Set())
  const [dismissedBanners, setDismissedBanners] = useState<Set<string>>(new Set())

  const handleRegenerateStart = useCallback((date: string) => {
    setRegeneratingDays(prev => new Set(prev).add(date))
  }, [])

  const handleRegenerateEnd = useCallback((date: string) => {
    setRegeneratingDays(prev => {
      const next = new Set(prev)
      next.delete(date)
      return next
    })
    setReplannedDays(prev => new Set(prev).add(date))
  }, [])

  const handleRegenerateError = useCallback((date: string) => {
    setRegeneratingDays(prev => {
      const next = new Set(prev)
      next.delete(date)
      return next
    })
  }, [])

  const handleUndoRegenerateDay = useCallback((date: string) => {
    setReplannedDays(prev => {
      const next = new Set(prev)
      next.delete(date)
      return next
    })
  }, [])

  const createUndoHandler = useCallback((date: string) => {
    return () => handleUndoRegenerateDay(date)
  }, [handleUndoRegenerateDay])

  useEffect(() => {
    try {
      const dismissed = getLocalStorage(SUGGEST_REPLAN_BANNER_DISMISSED_KEY) || []
      setDismissedBanners(new Set(dismissed))
    } catch {
      setDismissedBanners(new Set())
    }
  }, [])

  const handleDismissBanner = useCallback((itemId: string) => {
    setDismissedBanners(prev => {
      const newSet = new Set(prev).add(itemId)
      setLocalStorage(SUGGEST_REPLAN_BANNER_DISMISSED_KEY, Array.from(newSet))
      return newSet
    })
  }, [])

  const { heights } = useContext(HeaderHeightContext)

  const { canEdit } = useTripPermissions(tripId)

  // consequence of not being able to use the true height of the date carousel
  const isLargeDesktop = useScreenSizeUp('largeDesktop')
  const parentBottomPadding = isLargeDesktop ?
    LARGE_DESKTOP_WHITESPACE_OFFSET :
    0

  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => { setIsMounted(true) }, [])
  // from the white space that is required at the bottom of the stacked itinerary
  // get rid of the sticky headers and the size of the last date itinerary block
  const occupiedWhiteSpace =
    isMounted ?
      headerOffset + heights.tripPlannerFooter + parentBottomPadding :
      0

  const whiteSpaceStyle = {
    '--screenOnlyMinHeight': `calc(100vh - ${occupiedWhiteSpace}px)`,
  } as CSSProperties

  const isDragging = useRef(false)
  const [startPresentations, setStartPresentations] = useSyncedState(
    startItemsBySection,
    isDragging.current,
  )
  const days = useMemo(
    () => addIntermediateAndEndItems(startPresentations, items),
    [startPresentations, items],
  )

  const [activeItemData, setActiveItemData] = useState<{
    id: string
    html: string
    height: number
    startDate: string
    index: number
  } | null>(null)
  const [dropData, setDropData] = useState<{
    droppedItemId: string
    droppedPositionTop: number
    droppedTimestamp: number
    shouldStabilise: boolean
  } | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const indexedDays = useMemo(
    () => arrayToObject(days, (d) => itinerarySectionToString(d.date)),
    [days],
  )

  const { mutate: updateRank } = useRankTripItem()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 2,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 2,
      },
    }),
  )

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event

      const grabbedItem = rootRef.current?.querySelector(
        `#draggable-item-${active.id}`,
      )
      if (grabbedItem) {
        const clone = grabbedItem.cloneNode(true) as Element
        clone.id = ''
        const dayItems = days.find((d) =>
          d.startItems.some((item) => item.id === active.id),
        )!
        setActiveItemData({
          id: active.id as string,
          html: clone.outerHTML,
          height: grabbedItem.getBoundingClientRect().height,
          startDate: itinerarySectionToString(dayItems.date),
          index: dayItems.startItems.findIndex(
            (item) => item.id === active.id,
          ),
        })
      }
      isDragging.current = true
    },
    [days],
  )

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      isDragging.current = false

      if (!over || !activeItemData) {
        return
      }

      setActiveItemData(null)
      const overId = over.id as string
      const [targetDate, positionType, refItemId] = overId.split(':')

      const sourceDate = activeItemData.startDate

      // Save some data about where in the viewport the item was dropped
      const droppable = rootRef.current?.querySelector(
        `[data-droppableid="${overId}"]`,
      )
      if (droppable) {
        const viewportHeight = window.innerHeight
        const droppableRect = droppable.getBoundingClientRect()
        const droppableIsInView =
          droppableRect.top < viewportHeight && droppableRect.bottom > 0
        setDropData({
          droppedItemId: active.id as string,
          droppedPositionTop: droppableRect.top,
          droppedTimestamp: Date.now(),
          shouldStabilise: droppableIsInView,
        })
      }

      if (refItemId === active.id) {
        return
      }
      if (
        (positionType === 'after' || positionType === 'before') &&
        !refItemId
      ) {
        return
      }

      const sourceDayItems = indexedDays[sourceDate].startItems

      const item = sourceDayItems.find((i) => i.id === active.id)

      if (!item) {
        return
      }

      // Move the item to its new position in the local state
      setStartPresentations((prev) => {
        const withItemRemovedFromSourceDay = prev.map((d) => {
          if (itinerarySectionToString(d.date) === sourceDate) {
            return {
              ...d,
              startItems: d.startItems.filter(
                (item) => item.id !== active.id,
              ),
            }
          }
          return d
        })
        const targetDayItems = withItemRemovedFromSourceDay.find(
          (d) => itinerarySectionToString(d.date) === targetDate,
        )!.startItems

        let targetDayIndex: number
        if (positionType === 'start') {
          targetDayIndex = 0
        } else if (positionType === 'before') {
          targetDayIndex = targetDayItems.findIndex(
            (item) => item.id === refItemId,
          )
        } else if (positionType === 'after') {
          targetDayIndex =
            targetDayItems.findIndex((item) => item.id === refItemId) + 1
        } else {
          targetDayIndex = targetDayItems.length
        }

        return withItemRemovedFromSourceDay.map((d) => {
          if (itinerarySectionToString(d.date) === targetDate) {
            let updatedItem = item
            if (sourceDate !== NO_DATES) {
              const newStartDate = moment.isMoment(d.date) ? d.date.clone() : undefined
              updatedItem = updateItemStartDate(item, newStartDate)
            }

            return {
              ...d,
              startItems: [
                ...d.startItems.slice(0, targetDayIndex),
                updatedItem,
                ...d.startItems.slice(targetDayIndex),
              ],
            }
          }
          return d
        })
      })

      // API call
      if (targetDate === NO_DATES) {
        updateRank({
          tripId,
          tripItemId: active.id as string,
          rankSpec: {
            type: 'summary',
            date: null,
            position: {
              type: positionType,
              itemId: refItemId,
            } as Parameters<typeof updateRank>[0]['rankSpec']['position'],
          },
        })
      } else {
        updateRank({
          tripId,
          tripItemId: active.id as string,
          rankSpec: {
            type: 'itinerary',
            date: targetDate,
            position: {
              type: positionType,
              itemId: refItemId,
            } as Parameters<typeof updateRank>[0]['rankSpec']['position'],
          },
        })
      }
    },
    [activeItemData, indexedDays, setStartPresentations, tripId, updateRank],
  )

  /**
   * After a multi-date item is dropped, its intermediary/end items will be recalculated.
   * This can cause things to shift around as those cards disappear and reappear in their
   * new days. As a result, the item that the user just dragged might move from the position
   * they dropped it, which is a bad UX. This hook counteracts that by scrolling to match
   * the item's movement, keeping it stable in the viewport while things update.
   */
  useLayoutEffect(() => {
    if (
      dropData &&
      dropData.shouldStabilise &&
      Date.now() - dropData.droppedTimestamp < DROP_ANIMATION_DURATION
    ) {
      const droppedItem = rootRef.current?.querySelector(
        `#draggable-item-${dropData.droppedItemId}`,
      )
      if (droppedItem) {
        const topWhenDropped = dropData.droppedPositionTop
        const currentTop = droppedItem.getBoundingClientRect().top
        const diff = currentTop - topWhenDropped
        window.scrollBy({ top: diff, behavior: 'instant' })
      }
    }
  })
  /**
   * This is here to fix synchronisation issues. When an item starts being dragged, two things
   * need to happen at the same time: the item being dragged needs to collapse, and the drop
   * zone that it's over needs to expand. If they aren't in sync you get ugly flickering.
   * Unfortunately the library doesn't sync them up perfectly, so we need to do it ourselves.
   * Using this hook with { onDelay: 0 } provides one event-loop of delay which is enough.
   */
  const showAsDragging = useDelayedTogglingBoolean(!!activeItemData, {
    onDelay: 0,
  })

  return (
    <div ref={rootRef}>
      <DraggedItemHeightContext.Provider
        value={activeItemData && showAsDragging ? activeItemData.height : 0}
      >
        <InlineItemCreateProvider>
          <DndContext
            sensors={sensors}
            collisionDetection={itineraryCollisionDetection}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            autoScroll={{
              threshold: {
                x: 0, // Prevents horizontal scrolling
                y: 0.2,
              },
            }}
          >
            {days.map(
              ({ date, startItems, intermediaryItems, endItems }, idx) => {
                const dateString = itinerarySectionToString(date)
                const replanItem = startItems.find(i => i.suggestReplanDay && !dismissedBanners.has(i.id))

                if (date === SUGGESTED) {
                  return <ItineraryBlockTrigger
                    key={SUGGESTED}
                    onVisibilityChange={(isVisible) => onSectionVisibilityChange(SUGGESTED, isVisible)}
                    blockNumber={idx}
                    top={headerOffset + 20}
                  >
                    <SuggestedSection
                      items={startItems}
                      headerOffset={headerOffset}
                    />
                  </ItineraryBlockTrigger>
                }

                if (date === NO_DATES) {
                  return <DayContainer
                    id={itinerarySectionElementId(NO_DATES)}
                    key={NO_DATES}
                  >
                    <ItineraryBlockTrigger
                      onVisibilityChange={(isVisible) => onSectionVisibilityChange(NO_DATES, isVisible)}
                      blockNumber={idx}
                      top={headerOffset + 20}
                    >
                      <SectionHeading
                        title={intl.formatMessage({
                          id: 'UuwWY99ZhtDhN4qtJP05',
                          defaultMessage: 'Saved',
                          description: 'Section heading title for saved itinerary items without dates',
                        })}
                        section={NO_DATES}
                        top={headerOffset}
                      />
                      <SortableNonDateItems
                        items={startItems}
                        draggingItemId={activeItemData?.id}
                      />
                    </ItineraryBlockTrigger>
                  </DayContainer>
                }

                return (
                  <DayContainer
                    id={itinerarySectionElementId(date)}
                    key={dateString}
                    style={idx === days.length - 1 ? whiteSpaceStyle : undefined}
                  >
                    <ItineraryBlockTrigger
                      onVisibilityChange={(isVisible) => onSectionVisibilityChange(dateString, isVisible)}
                      blockNumber={idx}
                      top={headerOffset + 20}
                    >
                      <SectionHeading
                        title={date.format('dddd')}
                        subtitle={date.format(DMY_CASUAL_SHORT_FORMAT)}
                        section={date}
                        top={headerOffset}
                        endContent={canEdit && <RegenerateButton
                          tripId={tripId}
                          date={dateString}
                          startItems={startItems}
                          onRegenerateStart={handleRegenerateStart}
                          onRegenerateEnd={handleRegenerateEnd}
                          onRegenerateError={handleRegenerateError}
                        />}
                        isRegenerating={regeneratingDays.has(dateString)}
                      />
                      {replannedDays.has(dateString) && (
                        <ReplanFeedback
                          tripId={tripId}
                          date={dateString}
                          onUndo={createUndoHandler(dateString)}
                        />
                      )}
                      {replanItem &&
                        <SuggestReplanDayBanner
                          item={replanItem}
                          date={dateString}
                          onRegenerateStart={handleRegenerateStart}
                          onRegenerateEnd={handleRegenerateEnd}
                          handleDismissBanner={handleDismissBanner}
                        />}
                      {intermediaryItems && intermediaryItems.length > 0 && intermediaryItems.map((item) => (
                        <ItineraryInfoCard key={item.id} intermediateItem={item} date={date} />
                      ))}
                      {tripLoading && <LoadingDay />}
                      {!tripLoading && (
                        <SortableItineraryItems
                          trip={trip}
                          canEdit={canEdit}
                          startItems={startItems}
                          intermediaryItems={intermediaryItems}
                          endItems={endItems}
                          date={date}
                          draggingItemId={activeItemData?.id}
                          regeneratingDays={regeneratingDays}
                        />
                      )}
                    </ItineraryBlockTrigger>
                  </DayContainer>
                )
              },
            )}
            <DragOverlay
              dropAnimation={{
                duration: DROP_ANIMATION_DURATION,
                easing: 'ease-out',
                sideEffects: dropAnimationSideEffects,
              }}
              zIndex={zIndex.draggingItem}
              className="drag-overlay"
              modifiers={[restrictToItineraryDate]}
            >
              {activeItemData && (
                <>
                  <div
                    dangerouslySetInnerHTML={{ __html: activeItemData.html }}
                  />
                  <WhileDraggingStyle />
                </>
              )}
            </DragOverlay>
          </DndContext>
        </InlineItemCreateProvider>
      </DraggedItemHeightContext.Provider>
    </div>
  )
}

export default SortableItineraryDays
